import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';

import { IconType } from 'utils/types';

import { meetingTestingConstants } from '../../../../constants/src/index';
import { ReactComponent as Search } from '../../assets/icons/Search.svg';
import { ReactComponent as X } from '../../assets/icons/X.svg';
import Button from '../Button/Button';
import InputGroup from '../InputGroup/InputGroup';
import { InputSizeType, InputVariantType } from '../InputGroup/utils';

const SearchIcon: IconType = (props) => <Search data-testid="search-icon" {...props} />;
const ClearIcon: IconType = (props) => <X data-testid="x-icon" {...props} />;

interface SearchInputProps {
  reset?: () => void;
  onCancel?: () => void;
  onChange: (value: string) => void;
  onUnmount?: () => void;
  placeholder: string;
  className?: string;
  inputClassName?: string;
  variant?: InputVariantType;
  size?: InputSizeType;
  iconPlacement?: 'right' | 'left';
  iconClassName?: string;
  dataTestId?: string;
}

const SearchInput = ({
  reset,
  onCancel,
  onChange,
  onUnmount,
  placeholder,
  className,
  inputClassName,
  variant = 'regular',
  size = 'md',
  iconPlacement = 'right',
  iconClassName,
  dataTestId,
}: SearchInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const onChangeDebounced = useDebounce(onChange, 500);

  const handleChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setValue(value);
    return onChangeDebounced(value);
  };

  const handleCancel = () => {
    handleClick();
    if (onCancel) onCancel();
  };

  const handleClick = () => {
    setValue('');
    if (!reset) return onChange('');

    reset();
  };

  const sharedIconClass = cx('stroke-current stroke-gray-100 z-20', iconClassName);

  const inputIconsProps =
    iconPlacement === 'left'
      ? {
          icon: value ? ClearIcon : undefined,
          onIconClick: value ? handleClick : undefined,
          iconClassName: sharedIconClass,
          secondaryIcon: SearchIcon,
          secondaryIconClassName: sharedIconClass,
        }
      : {
          icon: value ? ClearIcon : SearchIcon,
          iconClassName: sharedIconClass,
          onIconClick: value ? handleClick : undefined,
        };

  useEffect(
    () => () => {
      if (onUnmount) onUnmount();
    },
    [onUnmount]
  );

  return (
    <div className={cx('flex w-full items-center space-x-2', className)}>
      <InputGroup size={size}>
        <InputGroup.Input
          onChange={handleChange}
          placeholder={placeholder}
          {...inputIconsProps}
          value={value}
          variant={variant}
          data-testid={dataTestId || meetingTestingConstants.searchInput}
          className={inputClassName}
        />
      </InputGroup>
      {onCancel && (
        <Button
          variant="text"
          size="xs"
          className="text-primary-50 border-none !p-0 !font-medium md:hidden"
          onClick={handleCancel}
        >
          {t('cancel')}
        </Button>
      )}
    </div>
  );
};

export default SearchInput;
